import { readUserInfo } from '../services/pageAccess';
import { formatLocalDate, orderDateToInputValue, parseLocalDateParts } from './dateLocal';

export const ACCOUNT_NATURE_OPTIONS = [
  { value: 'G', label: 'General' },
  { value: 'C', label: 'Customer' },
  { value: 'S', label: 'Supplier' },
  { value: 'CH', label: 'Cash' },
  { value: 'BK', label: 'Bank' },
  { value: 'PR', label: 'PDC Receivable' },
  { value: 'PP', label: 'PDC Payable' },
];

export function parseApiList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

export function parseKendoResult(response) {
  const rows = parseApiList(response);
  const total = Number(response?.Total ?? response?.total ?? rows.length);
  return { rows, total };
}

export function buildAccountFilter(term, field = 'Name') {
  return {
    filter: {
      logic: 'and',
      filters: [{ field, operator: 'contains', value: term, ignoreCase: true }],
    },
    page: 1,
    pageSize: 10,
  };
}

export function getFinancialYearId() {
  const user = readUserInfo();
  if (user?.FinancialYearId) return Number(user.FinancialYearId);
  try {
    const cp = JSON.parse(localStorage.getItem('companyProfile') || '{}');
    return Number(cp.FinancialYearId) || 0;
  } catch {
    return 0;
  }
}

export function parseNextVoucherNo(response) {
  if (response == null) return '';
  if (typeof response === 'string') return response.trim().replace(/^"|"$/g, '');
  if (typeof response === 'number') return String(response);
  if (typeof response === 'object') {
    if (response.VoucherNumber) return String(response.VoucherNumber).trim();
    if (response.value) return parseNextVoucherNo(response.value);
  }
  return String(response).trim();
}

export function formatTraceVoucherNo(trace) {
  if (!trace) return '';
  const prefix = String(trace.Prefix ?? '').trim();
  const nextNum = Number(trace.Number ?? 0) + 1;
  if (!prefix && !nextNum) return '';
  return `${prefix}${nextNum}`;
}

/** Read next voucher number from TraceNumbers table (same source as legacy document numbers). */
export async function fetchNextVoucherNumber(config, MainService) {
  if (!config?.traceNumberName) return '';

  try {
    const traces = await MainService.GetTraceNumbers({});
    const list = parseApiList(traces);
    const trace = list.find((row) => row.Name === config.traceNumberName);
    if (trace) {
      return formatTraceVoucherNo(trace);
    }
  } catch (err) {
    console.warn('Trace number lookup failed:', err);
  }

  try {
    const next = await MainService[config.apis.nextNo]();
    return parseNextVoucherNo(next);
  } catch (err) {
    console.warn('GetNext voucher API failed:', err);
    return '';
  }
}

export function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function mapSaveAccountResult(code) {
  const n = Number(code);
  if (n === 1) return { ok: true, message: 'Saved successfully' };
  if (n === 2) return { ok: false, message: 'Account limit reached' };
  if (n === 3) return { ok: false, message: 'Account name already exists' };
  return { ok: false, message: 'Save failed' };
}

export function emptyVoucherDetail(extra = {}) {
  return {
    Id: 0,
    AccountId: '',
    Amount: 0,
    Description: '',
    CostCenter1Id: '',
    CostCenter2Id: '',
    CostCenter3Id: '',
    Account: null,
    ChequeNo: '',
    MaturityDate: formatLocalDate(),
    ...extra,
  };
}

export function padVoucherDetails(details, detailKey, minRows = 5) {
  const list = Array.isArray(details) ? details.map((row) => ({ ...row })) : [];
  while (list.length < minRows) {
    list.push(emptyVoucherDetail());
  }
  return { [detailKey]: list };
}

export function voucherDateParts(dateInput) {
  const parts = parseLocalDateParts(dateInput || formatLocalDate());
  return {
    DateDay: parts.day,
    DateMonth: parts.month,
    DateYear: parts.year,
  };
}

export function normalizeVoucherFromApi(data, detailKey) {
  if (!data) return null;
  if (data.Message && !data.Id && !data.VoucherNumber) return null;
  const master = Array.isArray(data) ? data[0] : data;
  if (!master || !master.Id) return null;
  const details = master[detailKey] || [];
  return {
    ...master,
    dateInput: orderDateToInputValue(master.Date),
    chequeDateInput: master.ChequeDate ? orderDateToInputValue(master.ChequeDate) : '',
    maturityDateInput: master.MaturityDate ? orderDateToInputValue(master.MaturityDate) : formatLocalDate(),
    Account: master.Account || null,
    BankAccount: master.BankAccount || null,
    [detailKey]: details.map((row) => ({
      ...row,
      Account: row.Account || null,
      MaturityDate: row.MaturityDate ? orderDateToInputValue(row.MaturityDate) : formatLocalDate(),
    })),
  };
}

export function calcVoucherTotal(details) {
  return (details || []).reduce((sum, row) => {
    const amount = Number(row.Amount || 0);
    const accountId = Number(row.AccountId || 0);
    if (accountId > 0 && amount > 0) return sum + amount;
    return sum;
  }, 0);
}

export function validateVoucherMaster({ accountId, details }) {
  if (!Number(accountId)) return 'Select the cash/bank account';
  const active = (details || []).filter((row) => Number(row.AccountId) > 0 || Number(row.Amount) > 0);
  if (!active.length) return 'Add at least one voucher line';
  const invalid = active.some(
    (row) => (Number(row.AccountId) > 0 && Number(row.Amount) <= 0) || (!Number(row.AccountId) && Number(row.Amount) > 0)
  );
  if (invalid) return 'Each line needs both account and amount';
  const valid = active.filter((row) => Number(row.AccountId) > 0 && Number(row.Amount) > 0);
  if (!valid.length) return 'Add at least one valid voucher line';
  return '';
}

export async function printAccountingVoucher(voucherId, printType, MainService, getApiBaseUrl) {
  if (!voucherId || !printType) return false;
  const response = await MainService.PrintInvoice({
    PrintId: Number(voucherId),
    PrintType: printType,
  });
  const fileName = typeof response === 'string' ? response.trim().replace(/^"|"$/g, '') : '';
  if (!fileName) return false;
  window.open(`${getApiBaseUrl()}ReportFiles/${fileName}`, '_blank', 'noopener,noreferrer');
  return true;
}

export function buildVoucherSavePayload(master, config) {
  const detailKey = config.detailKey;
  const includeLineChequeFields = config.detailChequeFields && !config.hasChequeHeader;
  const details = (master[detailKey] || [])
    .filter((row) => Number(row.AccountId) > 0 && Number(row.Amount) > 0)
    .map((row) => {
      const line = {
        Id: row.Id || 0,
        AccountId: Number(row.AccountId),
        Amount: Number(row.Amount),
        Description: row.Description || '',
        CostCenter1Id: row.CostCenter1Id || null,
        CostCenter2Id: row.CostCenter2Id || null,
        CostCenter3Id: row.CostCenter3Id || null,
      };
      if (includeLineChequeFields) {
        line.ChequeNo = row.ChequeNo || '';
        line.MaturityDate = row.MaturityDate || null;
      }
      return line;
    });

  const payload = {
    Id: master.Id || 0,
    VoucherNumber: master.VoucherNumber || '',
    Remarks: master.Remarks || '',
    AccountId: Number(master.AccountId),
    Total: calcVoucherTotal(master[detailKey]),
    FinancialYearId: getFinancialYearId(),
    IsActive: true,
    ...voucherDateParts(master.dateInput),
    [detailKey]: details,
  };

  if (config.hasChequeHeader) {
    const maturityInput = master.maturityDateInput || formatLocalDate();
    const maturityParts = voucherDateParts(maturityInput);
    payload.ChequeNo = master.ChequeNo || '';
    payload.ChequeDate = master.chequeDateInput || null;
    payload.MaturityDate = maturityInput;
    payload.MDateDay = maturityParts.DateDay;
    payload.MDateMonth = maturityParts.DateMonth;
    payload.MDateYear = maturityParts.DateYear;
  }
  if (config.hasBankAccount) {
    payload.BankAccountId = master.BankAccountId ? Number(master.BankAccountId) : null;
  }

  return payload;
}
